import { useEffect, useRef, useState } from "react";
import { submitInquiry } from "../../services/contactService";
import { COMPANY, Icon, ICONS } from "./SiteChrome";

// The site's demo request (type DEMO, landing page) and contact form (type CONTACT, /contact).
// The backend emails it to the sales inbox (ContactService).

const TEAM_SIZES = ["Just me", "2–10", "11–50", "51–200", "200+"];

const EMPTY = { name: "", email: "", company: "", phone: "", teamSize: "", subject: "", message: "", website: "" };

const Field = ({ label, required, full, children }) => (
	<label className={`field${full ? " full" : ""}`}>
		<span className="field-label">
			{label}
			{required && <em aria-hidden="true"> *</em>}
		</span>
		{children}
	</label>
);

export default function InquiryForm({ type }) {
	const isDemo = type === "DEMO";
	const [values, setValues] = useState(EMPTY);
	const [status, setStatus] = useState("idle"); // idle | sending | sent
	const [error, setError] = useState("");
	const doneRef = useRef(null);

	// The form collapses into a short confirmation, which can end up above the viewport
	useEffect(() => {
		if (status === "sent") doneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
	}, [status]);

	const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

	const onSubmit = async (e) => {
		e.preventDefault();
		setStatus("sending");
		setError("");
		try {
			await submitInquiry({ type, ...values });
			setStatus("sent");
		} catch (err) {
			setError(err.response?.data?.message || `Something went wrong. Please email us at ${COMPANY.email}.`);
			setStatus("idle");
		}
	};

	if (status === "sent") {
		return (
			<div className="form-done" role="status" ref={doneRef}>
				<span className="form-done-icon">
					<Icon d={ICONS.check} size={22} stroke={3} />
				</span>
				<h3>{isDemo ? "Thanks — your demo request is in." : "Thanks — your message is on its way."}</h3>
				<p>
					{isDemo
						? `We'll email ${values.email} to find a time that suits you.`
						: `We'll reply to ${values.email} as soon as we can.`}
				</p>
				<button
					type="button"
					className="link"
					onClick={() => {
						setValues(EMPTY);
						setStatus("idle");
					}}
				>
					Send another
				</button>
			</div>
		);
	}

	return (
		<form className="inq-form" onSubmit={onSubmit}>
			<div className="field-grid">
				<Field label="Full name" required>
					<input value={values.name} onChange={set("name")} required maxLength={100} autoComplete="name" placeholder="Jane Doe" />
				</Field>
				<Field label={isDemo ? "Work email" : "Email address"} required>
					<input type="email" value={values.email} onChange={set("email")} required maxLength={254} autoComplete="email" placeholder="jane@company.com" />
				</Field>
				<Field label="Company">
					<input value={values.company} onChange={set("company")} maxLength={150} autoComplete="organization" placeholder="Your company" />
				</Field>
				{isDemo ? (
					<Field label="Team size">
						<select value={values.teamSize} onChange={set("teamSize")}>
							<option value="">Select…</option>
							{TEAM_SIZES.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</select>
					</Field>
				) : (
					<Field label="Phone number">
						<input type="tel" value={values.phone} onChange={set("phone")} maxLength={40} autoComplete="tel" placeholder="+91 98765 43210" />
					</Field>
				)}
				{!isDemo && (
					<Field label="Subject" required full>
						<input value={values.subject} onChange={set("subject")} required maxLength={200} placeholder="How can we help?" />
					</Field>
				)}
				<Field label={isDemo ? "What would you like to see?" : "Message"} required={!isDemo} full>
					<textarea
						value={values.message}
						onChange={set("message")}
						required={!isDemo}
						maxLength={5000}
						rows={isDemo ? 3 : 5}
						placeholder={isDemo ? "Optional — e.g. the app you test, or what breaks most often" : "Tell us a little about what you need"}
					/>
				</Field>
			</div>
			{/* Honeypot: hidden from people; bots that fill it in are dropped by the backend. */}
			<div className="hp" aria-hidden="true">
				<label>
					Website
					<input tabIndex={-1} autoComplete="off" value={values.website} onChange={set("website")} />
				</label>
			</div>
			{error && (
				<p className="form-error" role="alert">
					{error}
				</p>
			)}
			<button type="submit" className="btn btn-primary btn-lg form-submit" disabled={status === "sending"}>
				{status === "sending" ? "Sending…" : isDemo ? "Request my demo" : "Send message"}
				{status !== "sending" && <Icon d={ICONS.arrow} size={18} />}
			</button>
		</form>
	);
}
